import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import {
  AppDialogSection,
  AppDialog,
  AppDialogHeader,
  AppDialogTitle,
  AppDialogContent,
  AppDialogActions,
  AppDialogXBtn,
} from '@/UI/AppDialog/AppDialog';
import { TextSmRg, TextStdRg } from '@/UI/AppTypography/AppTypography';
import { Box } from '@/UI/Box/Box';
import AppDivider from '@/UI/AppDivider/AppDivider';
import { CaretIcon } from '@/UI/Icons/CaretIcon';
import { Cards3Icon } from '@/UI/Icons/Cards3Icon';
import { color } from '@/colors';
import { themeColor } from '@/UI/themeColors';
import { I18N_KEYS } from '@/language/keys';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { FolderScope } from '@/enums/folder-scope.enum';
import { useDashboardFolders } from '@/components/Dashboard/useDashboardFolders';
import { useWorkspaceFolders } from '@/components/Dashboard/useWorkspaceFolders';
import { SubscriptionType } from '@/types/shared';
import { useSubscriptionPermissions } from '@/hooks/useSubscriptionPermissions/useSubscriptionPermissions';
import { TeamIcon } from '@/UI/Icons/TeamIcon';
import { FlexCenVer, FlexCenVerSpaceBetween, FlexCol } from '@/UI/styles';
import { Tag } from '@/UI/Tag/Tag';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { ZIndex } from '@/consts/z-indexes.consts';
import { MoveModalFplContent } from '@/components/Dashboard/MoveModalFplContent';
import type { FolderResponseDto } from '@/types/folder.types';

interface MoveModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (targetFolderId: string | null, targetScope: FolderScope) => void | Promise<void>;
  itemName: string;
  itemId: string | null;
  isFolder: boolean;
  /** Overrides the item title. Folders keep their own title. */
  titleKey?: string;
  currentFolderId?: string | null;
  isWorkspaceScope?: boolean;
}

interface BreadcrumbItem {
  id: string | null;
  name: string;
  type?: 'dashboard' | 'scope' | 'folder';
  scope?: FolderScope;
}

// Build the chain of folders from the scope root down to (and including) the given folder.
const buildFolderStack = (folderId: string, allFolders: FolderResponseDto[]): FolderResponseDto[] => {
  const folder = allFolders.find((f) => f.id === folderId);
  if (!folder) return [];
  return folder.parentId ? [...buildFolderStack(folder.parentId, allFolders), folder] : [folder];
};

export const MoveModal = ({
  open,
  onClose,
  onConfirm,
  itemName,
  itemId,
  isFolder,
  titleKey,
  currentFolderId,
  isWorkspaceScope,
}: MoveModalProps) => {
  const { t } = useTranslation();
  const { folders: personalFolders } = useDashboardFolders();
  const { folders: workspaceFolders } = useWorkspaceFolders();
  const { isSubscriptionTypeIn } = useSubscriptionPermissions();
  const showWorkspaceOption = isSubscriptionTypeIn([SubscriptionType.Team, SubscriptionType.Enterprise]);
  const isFpl = useFplMigrationFlag();
  const currentScope = isWorkspaceScope ? FolderScope.WORKSPACE : FolderScope.PERSONAL;
  const [selectedScope, setSelectedScope] = useState<FolderScope>(
    isWorkspaceScope ? FolderScope.WORKSPACE : FolderScope.PERSONAL,
  );
  const folders = selectedScope === FolderScope.PERSONAL ? personalFolders : workspaceFolders;
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedRoot, setSelectedRoot] = useState(false);
  const [currentPath, setCurrentPath] = useState<BreadcrumbItem[]>([]);
  const [displayFolders, setDisplayFolders] = useState<FolderResponseDto[]>([]);
  const [hoveredFolderId, setHoveredFolderId] = useState<string | null>(null);
  // FPL-path navigation: stack of folders the user has drilled into within the active scope tab.
  const [fplStack, setFplStack] = useState<FolderResponseDto[]>([]);
  const fplInitializedRef = useRef(false);

  // Get the current location name
  const currentLocationName = currentFolderId
    ? folders.find((f) => f.id === currentFolderId)?.name ||
      (isWorkspaceScope ? t(I18N_KEYS.NAVIGATION_DRAWER.WORKSPACE_FILES) : t(I18N_KEYS.NAVIGATION_DRAWER.MY_FILES))
    : isWorkspaceScope
      ? t(I18N_KEYS.NAVIGATION_DRAWER.WORKSPACE_FILES)
      : t(I18N_KEYS.NAVIGATION_DRAWER.MY_FILES);

  // Initialize path when modal opens (MUI path only)
  useEffect(() => {
    if (open && !isFpl && currentPath.length === 0) {
      // Build initial path based on current location
      const initialPath: BreadcrumbItem[] = [
        { id: null, name: 'Dashboard', type: 'dashboard' },
        {
          id: null,
          name: isWorkspaceScope
            ? t(I18N_KEYS.NAVIGATION_DRAWER.WORKSPACE_FILES)
            : t(I18N_KEYS.NAVIGATION_DRAWER.MY_FILES),
          type: 'scope',
          scope: isWorkspaceScope ? FolderScope.WORKSPACE : FolderScope.PERSONAL,
        },
      ];

      // If item is in a folder, build the path to that folder
      if (currentFolderId) {
        const buildFolderPath = (folderId: string, folders: FolderResponseDto[]): BreadcrumbItem[] => {
          const folder = folders.find((f) => f.id === folderId);
          if (!folder) return [];

          if (folder.parentId) {
            return [...buildFolderPath(folder.parentId, folders), { id: folder.id, name: folder.name, type: 'folder' }];
          }
          return [{ id: folder.id, name: folder.name, type: 'folder' }];
        };

        const folderPath = buildFolderPath(currentFolderId, folders);
        initialPath.push(...folderPath);
      }

      setCurrentPath(initialPath);
      setSelectedRoot(false);
      setSelectedFolderId(null);
    }
  }, [open, isFpl, currentFolderId, folders, isWorkspaceScope, t, currentPath.length]);

  // Initialize the FPL path when the modal opens: land on the file's current scope tab, navigated
  // into the parent of the file's current folder so that folder shows in the list (with its badge).
  useEffect(() => {
    if (!open || !isFpl || fplInitializedRef.current) return;

    const currentFolder = currentFolderId ? folders.find((f) => f.id === currentFolderId) : null;
    // If the file lives in a folder that hasn't loaded yet, wait before initializing.
    if (currentFolderId && !currentFolder) return;

    const parentStack = currentFolder?.parentId ? buildFolderStack(currentFolder.parentId, folders) : [];

    fplInitializedRef.current = true;
    setSelectedScope(currentScope);
    if (parentStack.length > 0) {
      // Deeply nested: show breadcrumbs to the parent; the current folder appears in the list below.
      // Nothing is pre-selected — the user picks a target by clicking a row.
      setFplStack(parentStack);
      setSelectedFolderId(null);
      setSelectedRoot(false);
    } else {
      // At the scope root (current folder is top-level, or the file isn't in a folder). The scope
      // root is the default target so a top-level file can still be moved out to the root.
      setFplStack([]);
      setSelectedFolderId(null);
      setSelectedRoot(true);
    }
  }, [open, isFpl, currentScope, currentFolderId, folders]);

  // Determine what to display based on current path (MUI path only)
  useEffect(() => {
    if (open && !isFpl && currentPath.length > 0) {
      const lastPathItem = currentPath[currentPath.length - 1];

      // If we're at Dashboard level, don't show folders
      if (lastPathItem.type === 'dashboard') {
        setDisplayFolders([]);
      } else {
        // Otherwise show folders for the current path
        const currentPathId = lastPathItem.id;
        const filteredFolders = folders.filter(
          (folder) => folder.parentId === currentPathId && !(isFolder && folder.id === itemId),
        );
        setDisplayFolders(filteredFolders);
      }
    }
  }, [open, isFpl, currentPath, folders, isFolder, itemId]);

  const handleBreadcrumbClick = (index: number) => {
    const clickedItem = currentPath[index];

    // Navigate to the clicked breadcrumb
    setCurrentPath(currentPath.slice(0, index + 1));
    setSelectedFolderId(null);

    // If clicking on a scope breadcrumb, mark it as selected for move
    if (clickedItem.type === 'scope') {
      setSelectedRoot(true);
    } else {
      setSelectedRoot(false);
    }

    // If going back to dashboard, reset scope
    if (clickedItem.type === 'dashboard') {
      setSelectedScope(FolderScope.PERSONAL); // Default to personal
    }
  };

  const handleFolderClick = (folder: FolderResponseDto) => {
    setSelectedFolderId(folder.id);
    setSelectedRoot(false);
  };

  const handleFolderNavigate = (folder: FolderResponseDto) => {
    // Add to breadcrumb and navigate into folder
    setCurrentPath([...currentPath, { id: folder.id, name: folder.name, type: 'folder' }]);
    setSelectedFolderId(null);
    setSelectedRoot(false);
  };

  // FPL path: switching tabs resets to that scope's root, which becomes the move target.
  const handleFplTabChange = useCallback((scope: FolderScope) => {
    setSelectedScope(scope);
    setFplStack([]);
    setSelectedFolderId(null);
    setSelectedRoot(true);
  }, []);

  // FPL path: clicking a folder row selects it as the move target (does not drill in).
  const handleFplFolderSelect = useCallback((folder: FolderResponseDto) => {
    setSelectedFolderId(folder.id);
    setSelectedRoot(false);
  }, []);

  // FPL path: entering a folder (only when it has subfolders) drills into it (breadcrumbs)
  // and clears the current selection until a new target is picked.
  const handleFplFolderEnter = useCallback((folder: FolderResponseDto) => {
    setFplStack((prev) => [...prev, folder]);
    setSelectedFolderId(null);
    setSelectedRoot(false);
  }, []);

  // FPL path: navigate to a breadcrumb depth (0 = scope root, shows tabs again).
  const handleFplBreadcrumbNavigate = useCallback(
    (depth: number) => {
      const next = fplStack.slice(0, depth);
      setFplStack(next);
      setSelectedFolderId(null);
      setSelectedRoot(next.length === 0);
    },
    [fplStack],
  );

  const handleConfirm = useCallback(async () => {
    if (selectedRoot) {
      // Moving to root (My Files)
      setIsLoading(true);
      try {
        await onConfirm(null, selectedScope);
        // Don't close here - let the parent handle it after state update
      } catch (_error) {
        // Handle error silently
        setIsLoading(false);
      }
    } else if (selectedFolderId) {
      // Moving to selected folder
      setIsLoading(true);
      try {
        await onConfirm(selectedFolderId, selectedScope);
        // Don't close here - let the parent handle it after state update
      } catch (_error) {
        // Handle error silently
        setIsLoading(false);
      }
    }
  }, [selectedFolderId, selectedRoot, onConfirm, selectedScope]);

  // Handle scope selection from Dashboard
  const handleScopeSelect = (scope: FolderScope) => {
    setSelectedScope(scope);
    setSelectedFolderId(null);
    setSelectedRoot(true); // Select the scope root by default

    // Update path to include the selected scope
    setCurrentPath([
      { id: null, name: 'Dashboard', type: 'dashboard' },
      {
        id: null,
        name:
          scope === FolderScope.PERSONAL
            ? t(I18N_KEYS.NAVIGATION_DRAWER.MY_FILES)
            : t(I18N_KEYS.NAVIGATION_DRAWER.WORKSPACE_FILES),
        type: 'scope',
        scope: scope,
      },
    ]);
  };

  // Allow move when a folder is selected or when root is explicitly selected
  // But prevent moving to the same location in the same scope.
  // A scope is always active on the FPL path (the tab), so the scope guard only applies to MUI.
  const scopeSelected = isFpl ? true : currentPath.length > 1;
  const canMove =
    scopeSelected &&
    ((selectedFolderId !== null &&
      (selectedFolderId !== currentFolderId ||
        selectedScope !== (isWorkspaceScope ? FolderScope.WORKSPACE : FolderScope.PERSONAL))) ||
      (selectedRoot &&
        (currentFolderId !== null ||
          selectedScope !== (isWorkspaceScope ? FolderScope.WORKSPACE : FolderScope.PERSONAL))));

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setCurrentPath([]);
      setSelectedFolderId(null);
      setSelectedRoot(false);
      setDisplayFolders([]);
      setFplStack([]);
      fplInitializedRef.current = false;
    }
  }, [open]);

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      sx={{ zIndex: ZIndex.Overlays.CONFIRMATION_DIALOGS }}
      slotProps={{
        paper: {
          sx: {
            background: token.bg,
            borderRadius: 1,
            border: `1px solid ${color.White04_T}`,
            width: 400,
            maxWidth: 400,
            m: 0,
          },
        },
      }}
    >
      <AppDialogSection>
        <AppDialogHeader component="div" sx={{ p: 3, pt: 5.25, pb: 0, position: 'relative' }}>
          <AppDialogXBtn onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12, color: token.text }} />
          <AppDialogTitle>
            {t(
              isFolder
                ? I18N_KEYS.MAIN_DASHBOARD.MOVE_MODAL.TITLE_FOLDER
                : (titleKey ?? I18N_KEYS.MAIN_DASHBOARD.MOVE_MODAL.TITLE),
              {
                name: itemName,
              },
            )}
          </AppDialogTitle>
        </AppDialogHeader>

        <AppDialogContent sx={{ p: 0, mt: 1.5 }}>
          {isFpl ? (
            <MoveModalFplContent
              folders={folders}
              showWorkspaceOption={showWorkspaceOption}
              selectedScope={selectedScope}
              onTabChange={handleFplTabChange}
              fplStack={fplStack}
              selectedFolderId={selectedFolderId}
              onFolderSelect={handleFplFolderSelect}
              onFolderEnter={handleFplFolderEnter}
              onBreadcrumbNavigate={handleFplBreadcrumbNavigate}
              currentFolderId={currentFolderId}
              itemId={itemId}
              isFolder={isFolder}
            />
          ) : (
            <FlexCol sx={{ px: 3 }}>
              <FlexCenVer sx={{ gap: 1.5 }}>
                <TextSmRg sx={{ color: token.textSecondary }}>
                  {t(I18N_KEYS.MAIN_DASHBOARD.MOVE_MODAL.CURRENT_LOCATION)}
                </TextSmRg>
                <TextSmRg>{currentLocationName}</TextSmRg>
              </FlexCenVer>
              {/* Breadcrumbs */}
              <FlexCenVer sx={{ pt: 4 }}>
                {currentPath.map((item, index) => (
                  <FlexCenVer key={index}>
                    <TextSmRg
                      onClick={() => handleBreadcrumbClick(index)}
                      sx={{
                        color: index === currentPath.length - 1 ? token.text : token.textSecondary,
                        px: 0.75,
                        borderRadius: 0.5,
                        cursor: index === currentPath.length - 1 ? 'default' : 'pointer',
                        '&:hover': {
                          bgcolor: index === currentPath.length - 1 ? 'transparent' : color.White08_T,
                        },
                      }}
                    >
                      {item.name}
                    </TextSmRg>

                    {index < currentPath.length - 1 && (
                      <CaretIcon
                        width={12}
                        height={12}
                        color={token.text}
                        style={{ transform: 'rotate(-90deg)', marginLeft: 4, marginRight: 4 }}
                      />
                    )}
                  </FlexCenVer>
                ))}
              </FlexCenVer>

              <Box sx={{ my: 1, width: '100%' }}>
                <AppDivider />
              </Box>

              {/* Content based on current selection */}
              <FlexCol sx={{ maxHeight: '300px', overflowY: 'auto' }}>
                {currentPath.length === 1 ? (
                  <>
                    <FlexCenVer
                      onClick={() => handleScopeSelect(FolderScope.PERSONAL)}
                      sx={{
                        borderRadius: 1,
                        gap: 1,
                        py: 1,
                        px: 1.5,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: color.White04_T },
                      }}
                    >
                      <Cards3Icon width={20} height={20} />
                      <TextStdRg sx={{ color: token.text }}>{t(I18N_KEYS.NAVIGATION_DRAWER.MY_FILES)}</TextStdRg>
                    </FlexCenVer>

                    {showWorkspaceOption && (
                      <FlexCenVer
                        onClick={() => handleScopeSelect(FolderScope.WORKSPACE)}
                        sx={{
                          borderRadius: 1,
                          gap: 1,
                          py: 1,
                          px: 1.5,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: color.White04_T },
                        }}
                      >
                        <TeamIcon width={20} height={20} />
                        <TextStdRg sx={{ color: token.text }}>
                          {t(I18N_KEYS.NAVIGATION_DRAWER.WORKSPACE_FILES)}
                        </TextStdRg>
                      </FlexCenVer>
                    )}
                  </>
                ) : (
                  displayFolders.map((folder) => (
                    <FlexCenVerSpaceBetween
                      key={folder.id}
                      onMouseEnter={() => setHoveredFolderId(folder.id)}
                      onMouseLeave={() => setHoveredFolderId(null)}
                      onClick={() => handleFolderClick(folder)}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 1,
                        bgcolor: selectedFolderId === folder.id ? color.Black84 : 'transparent',
                        '&:hover': { bgcolor: selectedFolderId === folder.id ? color.Black84 : color.White04_T },
                      }}
                    >
                      <FlexCenVer sx={{ gap: 1, py: 1, px: 1.5, flex: 1 }}>
                        <TextStdRg sx={{ color: token.text }}>{folder.name}</TextStdRg>
                        {folder.id === currentFolderId && (
                          <Box component="span" sx={{ position: 'relative', top: '-1px' }}>
                            <Tag
                              text={t(I18N_KEYS.MAIN_DASHBOARD.MOVE_MODAL.CURRENT)}
                              textColor={color.White80_T}
                              bgColor={color.White08_T}
                            />
                          </Box>
                        )}
                      </FlexCenVer>
                      {hoveredFolderId === folder.id &&
                        folders.some((f) => f.parentId === folder.id && !(isFolder && f.id === itemId)) && (
                          <AppIconButton
                            aria-label={t(I18N_KEYS.GENERAL.ARIA.OPEN_NAMED_ITEM, { name: folder.name })}
                            size="medium"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFolderNavigate(folder);
                            }}
                            sx={{ mr: 0.5 }}
                          >
                            <CaretIcon width={16} height={16} style={{ transform: 'rotate(-90deg)' }} />
                          </AppIconButton>
                        )}
                    </FlexCenVerSpaceBetween>
                  ))
                )}
              </FlexCol>
            </FlexCol>
          )}
        </AppDialogContent>

        <AppDialogActions sx={{ justifyContent: 'flex-end', px: 3, pb: 3, pt: 4 }}>
          <ButtonContained mode="outlined" onClick={onClose} size="small" sx={{ px: 2 }}>
            {t(I18N_KEYS.MAIN_DASHBOARD.MOVE_MODAL.CANCEL)}
          </ButtonContained>
          <ButtonContained
            mode="filled-light"
            onClick={() => void handleConfirm()}
            disabled={isLoading || !canMove}
            size="small"
            loading={isLoading}
            sx={{
              px: 2,
              bgcolor: canMove ? themeColor.white100 : color.White04_T,
              color: canMove ? color.Black100 : color.White16_T,
              '&:hover': { bgcolor: canMove ? themeColor.white100 : color.White04_T, opacity: canMove ? 0.9 : 1 },
            }}
          >
            {t(I18N_KEYS.MAIN_DASHBOARD.MOVE_MODAL.MOVE)}
          </ButtonContained>
        </AppDialogActions>
      </AppDialogSection>
    </AppDialog>
  );
};
